import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Available color themes
export const COLOR_THEMES = {
  blue: {
    name: 'Μπλε',
    primary: '#004a99',
    primaryDark: '#003a7a',
    accent: '#0066cc'
  },
  green: {
    name: 'Πράσινο',
    primary: '#059669',
    primaryDark: '#047857',
    accent: '#10b981'
  },
  purple: {
    name: 'Μωβ',
    primary: '#7c3aed',
    primaryDark: '#6d28d9',
    accent: '#8b5cf6'
  },
  red: {
    name: 'Κόκκινο',
    primary: '#dc2626',
    primaryDark: '#b91c1c',
    accent: '#ef4444'
  },
  orange: {
    name: 'Πορτοκαλί',
    primary: '#ea580c',
    primaryDark: '#c2410c',
    accent: '#f97316'
  },
  teal: {
    name: 'Τυρκουάζ',
    primary: '#0d9488',
    primaryDark: '#0f766e',
    accent: '#14b8a6'
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : false;
  });

  const [colorTheme, setColorTheme] = useState(() => {
    const saved = localStorage.getItem('colorTheme');
    return saved && COLOR_THEMES[saved] ? saved : 'blue';
  });

  // Apply dark/light mode
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  // Apply color theme
  useEffect(() => {
    const theme = COLOR_THEMES[colorTheme];
    if (theme) {
      document.documentElement.style.setProperty('--color-primary', theme.primary);
      document.documentElement.style.setProperty('--color-primary-dark', theme.primaryDark);
      document.documentElement.style.setProperty('--color-accent', theme.accent);
      localStorage.setItem('colorTheme', colorTheme);
    }
  }, [colorTheme]);

  const toggleTheme = () => setIsDark(prev => !prev);
  
  const changeColorTheme = (themeName) => {
    if (COLOR_THEMES[themeName]) {
      setColorTheme(themeName);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      isDark, 
      toggleTheme, 
      colorTheme, 
      changeColorTheme,
      currentColors: COLOR_THEMES[colorTheme]
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
